import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useState } from "react";
import {
  LayoutDashboard,
  Building2,
  Briefcase,
  FileText,
  BarChart3,
  Bell,
  LogOut,
  Menu,
  X,
  ChevronDown,
  User,
  KeyRound,
  Shield,
  Wifi,
  WifiOff,
  Mail,
  Send,
  Settings,
  TrendingUp,
  UserCheck,
  FileBarChart,
  CalendarDays,
} from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { ScrollArea } from "../ui/scroll-area";
import { cn } from "../../lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";

export default function AppLayout() {
  const { user, logout, isAdmin, wsConnected, unreadCount } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const navigation = [
    {
      name: "Tableau de bord",
      href: "/",
      icon: LayoutDashboard,
      roles: ["admin_igt", "super_admin", "admin_igat", "cabinet", "auditeur"],
    },
    {
      name: "Cabinets",
      href: "/cabinets",
      icon: Building2,
      roles: ["admin_igt", "super_admin", "admin_igat", "auditeur"],
    },
    {
      name: "Administrateurs",
      href: "/administrateurs",
      icon: Shield,
      roles: ["super_admin", "admin_igt"],
    },
    {
      name: "Contrôleurs",
      href: "/controleurs",
      icon: UserCheck,
      roles: ["super_admin"],
    },
    {
      name: "Entreprises",
      href: "/entreprises",
      icon: Briefcase,
      roles: ["admin_igt", "super_admin", "admin_igat", "cabinet", "auditeur"],
    },
    {
      name: "Bilans Sociaux",
      href: "/bilans",
      icon: FileText,
      roles: ["admin_igt", "super_admin", "admin_igat", "cabinet", "auditeur"],
    },
    {
      name: "Statistiques",
      href: "/statistiques",
      icon: BarChart3,
      roles: ["admin_igt", "super_admin", "admin_igat", "auditeur"],
    },
    {
      name: "Analytics & IA",
      href: "/analytics",
      icon: TrendingUp,
      roles: ["admin_igt", "super_admin", "admin_igat", "auditeur"],
    },
    {
      name: "Calendrier",
      href: "/calendrier",
      icon: CalendarDays,
      roles: ["admin_igt", "super_admin", "admin_igat", "auditeur", "cabinet"],
    },
    {
      name: "Messagerie",
      href: "/messagerie",
      icon: Send,
      roles: ["admin_igt", "super_admin", "admin_igat"],
    },
    {
      name: "Historique Emails",
      href: "/historique-emails",
      icon: Mail,
      roles: ["admin_igt", "super_admin", "admin_igat", "cabinet"],
    },
    {
      name: "Paramètres Cabinet",
      href: "/parametres-cabinet",
      icon: Settings,
      roles: ["cabinet"],
    },
    {
      name: "Paramètres Système",
      href: "/parametres-systeme",
      icon: Settings,
      roles: ["super_admin", "admin_igt"],
    },
    {
      name: "Notifications",
      href: "/notifications",
      icon: Bell,
      roles: ["admin_igt", "super_admin", "admin_igat", "cabinet", "auditeur"],
      badge: unreadCount > 0 ? unreadCount : null,
    },
  ];

  const filteredNav = navigation.filter((item) =>
    item.roles.includes(user?.role)
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-slate-200">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                <span className="text-white font-heading font-bold text-lg">IGT</span>
              </div>
              <div>
                <p className="font-heading font-bold text-sm text-slate-900">Bilan Social</p>
                <p className="text-xs text-slate-500">Plateforme Officielle</p>
              </div>
            </Link>
            <button
              className="lg:hidden p-2 rounded-md hover:bg-slate-100"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {filteredNav.map((item) => {
                const isActive = location.pathname === item.href || 
                  (item.href !== "/" && location.pathname.startsWith(item.href));
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                      isActive
                        ? "bg-primary text-white"
                        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                    )}
                    data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.name}</span>
                    {item.badge && (
                      <span className="ml-auto bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          {/* User section */}
          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-50">
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">
                  {user?.prenom} {user?.nom}
                </p>
                <p className="text-xs text-slate-500 capitalize">
                  {user?.role === "super_admin" ? "Super Admin IGT" : 
                   user?.role === "admin_igt" ? "Admin IGT" : 
                   user?.role === "admin_igat" ? "Admin IGAT" : 
                   user?.role}
                </p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 bg-white border-b border-slate-200">
          <div className="flex items-center justify-between h-full px-4 lg:px-8">
            <button
              className="lg:hidden p-2 rounded-md hover:bg-slate-100"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </button>

            <div className="flex-1 lg:flex-none" />

            <div className="flex items-center gap-3">
              {/* Notifications */}
              <Link to="/notifications" className="relative p-2 rounded-md hover:bg-slate-100">
                <Bell className="w-5 h-5 text-slate-600" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                )}
              </Link>

              {/* User dropdown */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className={cn(
                      "w-2 h-2 rounded-full mr-2",
                      wsConnected ? "bg-green-500" : "bg-red-500"
                    )} />
                  </TooltipTrigger>
                  <TooltipContent>
                    {wsConnected ? "Connecté en temps réel" : "Reconnexion..."}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium">{user?.prenom} {user?.nom}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                    <div className="flex items-center gap-1 mt-1">
                      {wsConnected ? (
                        <Wifi className="w-3 h-3 text-green-500" />
                      ) : (
                        <WifiOff className="w-3 h-3 text-red-500" />
                      )}
                      <span className="text-xs text-muted-foreground">
                        {wsConnected ? "Temps réel actif" : "Hors ligne"}
                      </span>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/changer-mot-de-passe")} data-testid="change-password-btn">
                    <KeyRound className="w-4 h-4 mr-2" />
                    Changer le mot de passe
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8 pb-20">
          <Outlet />
        </main>

        {/* Footer with logos - visible on all pages */}
        <footer className="fixed bottom-0 left-64 right-0 bg-white/95 backdrop-blur-sm border-t border-slate-200 py-2 px-4 z-20 hidden lg:flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_igtsocial/artifacts/uyugnatb_photo_5782873435288571226_y.jpg" 
              alt="METPS"
              className="h-8 object-contain"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_5cb9c623-ed0c-4bae-a472-1c87173c2e5e/artifacts/papge646_t%C3%A9l%C3%A9chargement.jpeg" 
              alt="Simandou 2040"
              className="h-8 object-contain"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_5cb9c623-ed0c-4bae-a472-1c87173c2e5e/artifacts/vilkwd0e_branding.png" 
              alt="Guinée"
              className="h-8 object-contain"
            />
          </div>
          <div className="text-xs text-slate-500">
            <a 
              href="https://www.okcgn.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-primary transition-colors"
            >
              Powered by <span className="font-semibold text-primary">ALO.sa</span>
            </a>
          </div>
        </footer>
        {/* Mobile footer */}
        <footer className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-slate-200 py-2 px-4 z-10 flex lg:hidden items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_igtsocial/artifacts/uyugnatb_photo_5782873435288571226_y.jpg" 
              alt="METPS"
              className="h-6 object-contain"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_5cb9c623-ed0c-4bae-a472-1c87173c2e5e/artifacts/papge646_t%C3%A9l%C3%A9chargement.jpeg" 
              alt="Simandou 2040"
              className="h-6 object-contain"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_5cb9c623-ed0c-4bae-a472-1c87173c2e5e/artifacts/vilkwd0e_branding.png" 
              alt="Guinée"
              className="h-6 object-contain"
            />
          </div>
          <div className="text-xs text-slate-500">
            <a 
              href="https://www.okcgn.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-primary transition-colors"
            >
              Powered by <span className="font-semibold text-primary">ALO.sa</span>
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
}
